import { useToast } from '@chakra-ui/react';
import React from 'react';

import { DEFAULT_TOAST_DURATION, ToastTypes } from '@/constants/defaults';

/**
 * One retry per mount for a getDepartments cache entry that failed on its initial fetch. Shared across every
 * departments dropdown that needs its own retry/toast (e.g. the app-wide entry and any flag-gated excludeMRC
 * entry) without duplicating this logic - callers pass their own toastId so notifications don't collide.
 *
 * Reacts to isError rather than sampling it once at mount: a query that only starts fetching when its own
 * component mounts (e.g. a flag-gated entry skipped until then) is still isError=false on that first render,
 * so a mount-only sample would miss the failure entirely.
 *
 * hasRetried is set synchronously before calling refetch, not after it settles, and deliberately does not use
 * an isCancelled flag tied to this effect's own cleanup: refetch() itself makes isError go false (pending)
 * then true again once it resettles, and since that is this effect's own dependency, an isCancelled captured
 * in that cleanup would mark the very retry this effect just started as cancelled before it ever resolves,
 * permanently swallowing its outcome. isUnmountedRef instead only flips on a real unmount, so the toast still
 * fires for a retry that outlives a spurious dependency-triggered re-run of this same mounted instance.
 */
const useDepartmentsRetryOnMountError = (
  isError: boolean | undefined,
  refetch: () => { unwrap: () => Promise<unknown> },
  toast: ReturnType<typeof useToast>,
  toastId: string,
  notifyOnFailure = true,
) => {
  const hasRetried = React.useRef(false);
  const isUnmountedRef = React.useRef(false);

  React.useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isError || hasRetried.current) return;

    hasRetried.current = true;

    refetch()
      .unwrap()
      .catch(() => {
        if (isUnmountedRef.current || !notifyOnFailure || toast.isActive(toastId)) return;

        toast({
          description: 'Error loading departments - Network Error',
          duration: DEFAULT_TOAST_DURATION,
          id: toastId,
          isClosable: true,
          position: 'top',
          status: ToastTypes.ERROR,
          title: 'Unable to load departments',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);
};

export default useDepartmentsRetryOnMountError;
